import { Button, Table, Tag, message } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { serverUrl } from "../nestserver";
import ProgressCardStats from "./ProgressCardStats";
import CombinedProgressCardModal from "./CombinedProgressCardView";

const authHeaders = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

/**
 * The year-end combined progress cards that have been shared with a family.
 * Parents pass the chosen `studentId`; a student's own login needs none, the
 * server uses who is signed in. Draws nothing until there is a card.
 */
const CombinedProgressCards = ({ studentId, canAcknowledge = false }) => {
  const queryClient = useQueryClient();
  const [viewId, setViewId] = useState(null);
  const queryKey = ["progress-card-combined-by-student", studentId ?? "self"];

  const cards = useQuery(
    queryKey,
    async () => {
      const res = await axios.get(
        `${serverUrl}/progress-card-combined-v2/by-student/`,
        {
          headers: authHeaders(),
          params: studentId ? { studentId } : undefined,
        },
      );
      return res.data?.result ?? [];
    },
    { enabled: studentId !== null },
  );

  const handleDownload = async (record) => {
    try {
      const res = await axios.get(
        `${serverUrl}/progress-card-combined-v2/${record._id}/download`,
        { headers: authHeaders(), responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "AnnualProgressCard.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error("Could not download the progress card");
    }
  };

  const handleAcknowledge = async (record) => {
    try {
      await axios.patch(
        `${serverUrl}/progress-card-combined-v2/${record._id}/acknowledge`,
        {},
        { headers: authHeaders() },
      );
      message.success("Acknowledged");
      queryClient.invalidateQueries(queryKey);
    } catch (error) {
      message.error("Could not acknowledge the progress card");
    }
  };

  if (!cards.data?.length) return null;

  const columns = [
    {
      title: "Year-end Progress Card",
      key: "terms",
      render: (record) => (
        <div>
          <div className="text-base capitalize">
            {record.combination_name ||
              (record.terms ?? []).map((t) => t.name).join(" + ")}
          </div>
          <div className="text-xs text-gray-500">{record.year}</div>
        </div>
      ),
    },
    {
      title: "Total Marks",
      key: "total",
      align: "center",
      render: (record) =>
        record.max_marks
          ? `${record.total_marks} / ${record.max_marks}`
          : record.total_marks,
    },
    {
      title: "Rank & Attendance",
      key: "stats",
      render: (record) => <ProgressCardStats card={record} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => <Tag className="capitalize">{status}</Tag>,
    },
    {
      title: "Date",
      dataIndex: "notified_at",
      key: "notified_at",
      align: "center",
      render: (value) => (value ? moment(value).format("DD-MM-YYYY") : "--"),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (record) => (
        <div className="flex gap-2 justify-center">
          <Button size="small" onClick={() => setViewId(record._id)}>
            View
          </Button>
          <Button size="small" onClick={() => handleDownload(record)}>
            Download
          </Button>
          {canAcknowledge && record.status === "notified" ? (
            <Button
              size="small"
              type="primary"
              onClick={() => handleAcknowledge(record)}
            >
              Acknowledge
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="pt-6">
      <CombinedProgressCardModal id={viewId} onClose={() => setViewId(null)} />
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={cards.data}
        pagination={false}
      />
    </div>
  );
};

export default CombinedProgressCards;
